/* =====================================================================
   Ivy Row — Homepage banner (wh-hero) and promise strip (wh-promise)
   Loaded deferred after wh-core.js (window.WH). ES2017 max, no modules.

   Banner: auto-play paced by the progress bar (end of the CSS animation
   "wh-hero-fill" = next slide). Pauses on hover, keyboard focus, touch,
   hidden tab, banner off-screen, pause button, or block selected in the
   editor. Never auto-plays when the visitor prefers reduced motion.
   Arrows, Home, End in the slide list; swipe; subtle pointer parallax
   (desktop). Promise strip: scroll duration matched to width (constant
   speed), pauses on touch and off-screen.
   ===================================================================== */
(function () {
  'use strict';

  if (!window.WH) return;
  var WH = window.WH;

  var mqFine = window.matchMedia ? window.matchMedia('(hover: hover) and (pointer: fine)') : null;
  var mqDesk = window.matchMedia ? window.matchMedia('(min-width: 1024px)') : null;
  var instances = [];

  function focusVisible(el) {
    try {
      return el.matches(':focus-visible');
    } catch (err) {
      return true;
    }
  }

  function unbindAll(list) {
    for (var i = 0; i < list.length; i++) {
      list[i][0].removeEventListener(list[i][1], list[i][2], list[i][3]);
    }
  }

  /* ---------- Banner ---------- */
  function createHero(root) {
    var slidesWrap = root.querySelector('[data-wh-hero-slides]');
    if (!slidesWrap) return null;

    var slides = WH.all('[data-wh-hero-slide]', slidesWrap);
    var count = slides.length;
    var tabs = WH.all('[data-wh-hero-tab]', root);
    var tablist = root.querySelector('[role="tablist"]');
    var toggle = root.querySelector('[data-wh-hero-toggle]');
    var wanted = root.getAttribute('data-autoplay') === 'true';
    var speed = parseFloat(root.getAttribute('data-speed'));
    var current = 0;
    var startedAt = Date.now();
    var reasons = {
      user: false,
      hover: false,
      focus: false,
      touch: false,
      hidden: !!document.hidden,
      view: true,
      editor: false
    };
    var bound = [];
    var leaveTimer = 0;
    var frame = 0;
    var pointer = null;
    var touch = null;
    var stopView = null;
    var offReduced = null;
    var pending = false;

    if (!(speed >= 3)) speed = 7;

    for (var k = 0; k < count; k++) {
      if (slides[k].classList.contains('is-active')) {
        current = k;
        break;
      }
    }

    function listen(el, type, fn, opts) {
      if (!el) return;
      var options = opts || false;
      el.addEventListener(type, fn, options);
      bound.push([el, type, fn, options]);
    }

    function autoplay() {
      return wanted && count > 1 && !WH.reduced;
    }

    function paused() {
      return reasons.user || reasons.hover || reasons.focus || reasons.touch ||
        reasons.hidden || reasons.view || reasons.editor;
    }

    function render() {
      var auto = autoplay();
      var stop = paused();
      root.classList.toggle('is-auto', auto);
      root.classList.toggle('is-paused', auto && stop);
      root.classList.toggle('is-user-paused', auto && reasons.user);
      if (toggle) {
        toggle.hidden = !auto;
        toggle.setAttribute('aria-label', reasons.user
          ? 'Resume automatic slideshow'
          : 'Pause automatic slideshow');
      }
      if (count > 1) slidesWrap.setAttribute('aria-live', auto && !stop ? 'off' : 'polite');
    }

    function setReason(key, value) {
      if (reasons[key] === value) return;
      reasons[key] = value;
      render();
      if (pending && autoplay() && !paused()) go(current + 1, false);
    }

    function go(index, focusTab) {
      if (count < 2) return;
      pending = false;
      var next = ((index % count) + count) % count;
      if (next === current) {
        if (focusTab && tabs[next]) tabs[next].focus();
        return;
      }
      var prev = current;
      current = next;
      for (var i = 0; i < count; i++) {
        var on = i === next;
        var slide = slides[i];
        slide.classList.toggle('is-active', on);
        slide.classList.toggle('is-leaving', i === prev);
        if (on) {
          slide.removeAttribute('aria-hidden');
          slide.removeAttribute('inert');
        } else {
          slide.setAttribute('aria-hidden', 'true');
          slide.setAttribute('inert', '');
        }
        if (tabs[i]) {
          tabs[i].classList.toggle('is-current', on);
          tabs[i].classList.toggle('is-done', i < next);
          tabs[i].setAttribute('aria-selected', on ? 'true' : 'false');
          tabs[i].setAttribute('tabindex', on ? '0' : '-1');
        }
      }
      root.classList.toggle('is-ui-dark', slides[next].hasAttribute('data-wh-hero-dark'));
      window.clearTimeout(leaveTimer);
      leaveTimer = window.setTimeout(function () {
        slides[prev].classList.remove('is-leaving');
      }, 900);
      startedAt = Date.now();
      if (focusTab && tabs[next]) tabs[next].focus();
    }

    function onAnimationEnd(e) {
      if (e.animationName !== 'wh-hero-fill') return;
      var tab = tabs[current];
      if (!tab || !tab.contains(e.target)) return;
      if (!autoplay()) return;
      if (Date.now() - startedAt < speed * 500) return;
      if (paused()) {
        pending = true;
        return;
      }
      go(current + 1, false);
    }

    function onTabClick(e) {
      var tab = e.target && e.target.closest ? e.target.closest('[data-wh-hero-tab]') : null;
      var i = tab ? tabs.indexOf(tab) : -1;
      if (i > -1) go(i, false);
    }

    function onTabKey(e) {
      var i = tabs.indexOf(e.target);
      if (i < 0) return;
      var key = e.key;
      var to = -1;
      if (key === 'ArrowRight' || key === 'Right') to = (i + 1) % count;
      else if (key === 'ArrowLeft' || key === 'Left') to = (i - 1 + count) % count;
      else if (key === 'Home') to = 0;
      else if (key === 'End') to = count - 1;
      if (to < 0) return;
      e.preventDefault();
      go(to, true);
    }

    function onToggle() {
      setReason('user', !reasons.user);
    }

    function canParallax() {
      return !!(mqFine && mqFine.matches && mqDesk && mqDesk.matches) && !WH.reduced;
    }

    function applyPointer() {
      frame = 0;
      if (!pointer) return;
      var rect = root.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      var mx = Math.max(-1, Math.min(1, ((pointer.x - rect.left) / rect.width) * 2 - 1));
      var my = Math.max(-1, Math.min(1, ((pointer.y - rect.top) / rect.height) * 2 - 1));
      root.style.setProperty('--wh-hero-mx', mx.toFixed(3));
      root.style.setProperty('--wh-hero-my', my.toFixed(3));
    }

    function resetPointer() {
      pointer = null;
      if (frame) {
        window.cancelAnimationFrame(frame);
        frame = 0;
      }
      root.style.removeProperty('--wh-hero-mx');
      root.style.removeProperty('--wh-hero-my');
    }

    function onPointerMove(e) {
      if (e.pointerType !== 'mouse' || !canParallax()) return;
      pointer = { x: e.clientX, y: e.clientY };
      if (!frame) frame = window.requestAnimationFrame(applyPointer);
    }

    function onPointerEnter(e) {
      if (e.pointerType === 'mouse') setReason('hover', true);
    }

    function onPointerLeave(e) {
      if (e.pointerType !== 'mouse') return;
      setReason('hover', false);
      resetPointer();
    }

    function onFocusIn(e) {
      if (focusVisible(e.target)) setReason('focus', true);
    }

    function onFocusOut(e) {
      var to = e.relatedTarget;
      if (!to || !root.contains(to)) setReason('focus', false);
    }

    function onTouchStart(e) {
      if (!e.touches || e.touches.length !== 1) {
        touch = null;
        return;
      }
      touch = { x: e.touches[0].clientX, y: e.touches[0].clientY, dx: 0, dy: 0 };
      setReason('touch', true);
    }

    function onTouchMove(e) {
      if (!touch || !e.touches || e.touches.length !== 1) return;
      touch.dx = e.touches[0].clientX - touch.x;
      touch.dy = e.touches[0].clientY - touch.y;
    }

    function onTouchEnd() {
      var t = touch;
      touch = null;
      setReason('touch', false);
      if (t && Math.abs(t.dx) > 44 && Math.abs(t.dx) > Math.abs(t.dy) * 1.3) {
        go(current + (t.dx < 0 ? 1 : -1), false);
      }
    }

    function onTouchCancel() {
      touch = null;
      setReason('touch', false);
    }

    function onVisibility() {
      setReason('hidden', !!document.hidden);
    }

    function slideIndexOf(target) {
      for (var i = 0; i < count; i++) {
        if (slides[i] === target || slides[i].contains(target)) return i;
      }
      return -1;
    }

    function onBlockSelect(e) {
      var i = slideIndexOf(e.target);
      if (i < 0) return;
      go(i, false);
      setReason('editor', true);
    }

    function onBlockDeselect(e) {
      if (slideIndexOf(e.target) > -1) setReason('editor', false);
    }

    function destroy() {
      unbindAll(bound);
      bound = [];
      if (stopView) stopView();
      if (offReduced) offReduced();
      stopView = null;
      offReduced = null;
      window.clearTimeout(leaveTimer);
      if (frame) window.cancelAnimationFrame(frame);
      frame = 0;
      root.__whHero = null;
    }

    listen(root, 'animationend', onAnimationEnd);
    listen(tablist, 'click', onTabClick);
    listen(tablist, 'keydown', onTabKey);
    listen(toggle, 'click', onToggle);
    listen(root, 'pointerenter', onPointerEnter);
    listen(root, 'pointerleave', onPointerLeave);
    listen(root, 'pointermove', onPointerMove, { passive: true });
    listen(root, 'focusin', onFocusIn);
    listen(root, 'focusout', onFocusOut);
    listen(slidesWrap, 'touchstart', onTouchStart, { passive: true });
    listen(slidesWrap, 'touchmove', onTouchMove, { passive: true });
    listen(slidesWrap, 'touchend', onTouchEnd, { passive: true });
    listen(slidesWrap, 'touchcancel', onTouchCancel, { passive: true });
    listen(document, 'visibilitychange', onVisibility);
    listen(document, 'shopify:block:select', onBlockSelect);
    listen(document, 'shopify:block:deselect', onBlockDeselect);

    offReduced = WH.onReduced(function () {
      if (WH.reduced) resetPointer();
      render();
    });

    render();

    stopView = WH.inView(root, function () {
      root.classList.add('is-live');
      setReason('view', false);
    }, function () {
      root.classList.remove('is-live');
      setReason('view', true);
    }, { threshold: 0.15 });

    root.classList.add('is-ready');

    return {
      root: root,
      go: go,
      destroy: destroy
    };
  }

  /* ---------- Promise strip ---------- */
  function createPromise(root) {
    var list = root.querySelector('.wh-promise__list');
    if (!list) return null;

    var bound = [];
    var holdTimer = 0;
    var stopView = null;

    function listen(el, type, fn, opts) {
      var options = opts || false;
      el.addEventListener(type, fn, options);
      bound.push([el, type, fn, options]);
    }

    function measure() {
      var width = list.getBoundingClientRect().width;
      if (width > 0) root.style.setProperty('--wh-promise-dur', Math.max(16, Math.round(width / 32)) + 's');
    }

    function hold() {
      window.clearTimeout(holdTimer);
      root.classList.add('is-held');
    }

    function release() {
      window.clearTimeout(holdTimer);
      holdTimer = window.setTimeout(function () {
        root.classList.remove('is-held');
      }, 1500);
    }

    var onResize = WH.debounce(measure, 200);

    function destroy() {
      unbindAll(bound);
      bound = [];
      if (stopView) stopView();
      stopView = null;
      window.clearTimeout(holdTimer);
      root.__whPromise = null;
    }

    listen(root, 'touchstart', hold, { passive: true });
    listen(root, 'touchend', release, { passive: true });
    listen(root, 'touchcancel', release, { passive: true });
    listen(window, 'resize', onResize);
    listen(window, 'load', measure);

    root.classList.add('is-off');
    stopView = WH.inView(root, function () {
      root.classList.remove('is-off');
    }, function () {
      root.classList.add('is-off');
    }, { threshold: 0 });

    measure();
    if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') {
      document.fonts.ready.then(function () {
        if (root.__whPromise) measure();
      });
    }

    return {
      root: root,
      destroy: destroy
    };
  }

  /* ---------- Init (load and editor) ---------- */
  WH.ready(function (scope) {
    WH.all('[data-wh-hero]', scope).forEach(function (el) {
      if (el.__whHero) return;
      var api = createHero(el);
      if (!api) return;
      el.__whHero = api;
      instances.push(api);
    });
    WH.all('[data-wh-promise]', scope).forEach(function (el) {
      if (el.__whPromise) return;
      var api = createPromise(el);
      if (!api) return;
      el.__whPromise = api;
      instances.push(api);
    });
  });

  WH.unload(function (section) {
    instances = instances.filter(function (api) {
      if (section === api.root || section.contains(api.root)) {
        api.destroy();
        return false;
      }
      return true;
    });
  });
})();
